import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useQuery } from '@tanstack/react-query'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeExternalLinks from 'rehype-external-links'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { DocumentsListItem } from '../DocumentsListItem'
import { reverseUrl, ViewNames } from '../../../../navigation/reverseUrl'
import { Breadcrumb, BreadcrumbItemData } from '../../../common/Breadcrumb'
import { IconChoices } from '../../../common/Icon'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { DocumentsListEmptyState } from './DocumentsListEmptyState'
import { IconToggle } from '../../../common/IconToggle'
import { LocalStorageKeys } from '../../../../types/local-storage'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { documentQueries } from '../../../../api/documents'
import {
  getAllSubDocuments,
  getRootDocument,
} from '../../../../state/stores/documents'
import { Image } from '../../../common/Image'
import { ImagePreviewModal } from '../../../common/ImagePreviewModal'
import { useModal } from '../../../../hooks/useModal'
import { i18nSocietyType } from '../../../../i18n'

interface DocumentsListProps {
  section: 'feed' | 'society' | 'board' | 'society-website'
  rootRoute: ViewNames
  documentRoute: ViewNames
  rootSubDocuments: IDocumentModel[]
  documents: IDocumentModel[]
  noSearchResults: boolean
  document: IDocumentModel | undefined
  documentId?: string
}

export const DocumentsList = ({
  section,
  documentId,
  rootRoute,
  documentRoute,
  rootSubDocuments,
  documents,
  noSearchResults,
  document,
}: DocumentsListProps): ReactNode => {
  const { society } = useCurrentSociety()
  const { data: societyDocuments } = useQuery(
    documentQueries.getPerSocietyAndFolder(
      society?._id || '',
      documentId || society?.documentsRootFolderId || ''
    )
  )
  const rootFolderId =
    section === 'board'
      ? society?.boardDocumentsRootFolderId
      : society?.documentsRootFolderId

  const hasChildDocuments = useMemo(
    () =>
      documents.some((doc) =>
        documentId ? doc.parentId === documentId : doc.parentId === rootFolderId
      ),
    [documents, documentId, rootFolderId]
  )
  const { translate } = useAppTranslation()
  const [showFullDescription, setShowFullDescription] =
    useState(!hasChildDocuments)
  const {
    show: showImagePreviewModal,
    close: closeImagePreviewModal,
    open: openImagePreviewModal,
  } = useModal()
  const [isListMode, setIsListMode] = useState(
    localStorage.getItem(LocalStorageKeys.DOCUMENTS_LIST_MODE)
  )

  useEffect(() => {
    setShowFullDescription(!hasChildDocuments)
  }, [hasChildDocuments, documentId])

  const onChangeListMode = (_isListMode: boolean): void => {
    const updatedValue = _isListMode ? 'list' : 'card'
    localStorage.setItem(LocalStorageKeys.DOCUMENTS_LIST_MODE, updatedValue)
    setIsListMode(updatedValue)
  }

  const getSubDocuments = (): IDocumentModel[] => {
    if (documentId) {
      return getAllSubDocuments(societyDocuments || [], documentId)
    }

    return rootSubDocuments
  }

  const getRootPath = (): string => {
    return reverseUrl(rootRoute, { id: society?._id })
  }

  const getDocumentPath = (docId: string): string => {
    return reverseUrl(documentRoute, {
      id: society?._id,
      documentId: docId,
    })
  }

  const findAllParents = (
    currentDocument: IDocumentModel,
    previousParents: IDocumentModel[]
  ): IDocumentModel[] => {
    if (!currentDocument?.parentId) {
      return previousParents
    }
    const parent = rootSubDocuments.find(
      (doc) => doc._id === currentDocument.parentId
    ) as IDocumentModel
    if (!parent) {
      return previousParents
    }
    return findAllParents(parent, [parent, ...previousParents])
  }

  const getAllParentDocuments = (): IDocumentModel[] => {
    if (documentId) {
      const currentDocument = societyDocuments?.find(
        (doc) => doc._id === documentId
      )
      if (!currentDocument) return []
      const children = getSubDocuments()
      if (children.length > 0) {
        return findAllParents(children[0], [])
      }
      // No children, so mocking a fake child so the empty folder think it's a parent
      const mockDocument = {
        parentId: currentDocument._id,
        section,
      } as IDocumentModel
      return findAllParents(mockDocument, [])
    }
    return []
  }

  const getAllDocumentBreadcrumbs = (): BreadcrumbItemData[] => {
    const allParentDocuments = getAllParentDocuments()
    const parentBreadcrumbs = allParentDocuments.map((_document) => ({
      _id: _document._id,
      url: getDocumentPath(_document._id),
      title: _document.name || _document.filename,
    }))
    const rootDocument = getRootDocument({
      societyDocuments: societyDocuments || [],
      section,
    })

    const rootBreadcrumb = {
      _id: rootDocument ? rootDocument._id : 'root',
      url: getRootPath(),
      title: 'Root',
    }
    return [rootBreadcrumb, ...parentBreadcrumbs]
  }

  const breadcrumbs = getAllDocumentBreadcrumbs()

  const handleFullDescriptionToggle = (): void => {
    setShowFullDescription((prev) => !prev)
  }

  const _isListMode = isListMode === 'list'

  const shouldShowViewMore =
    document?.description && document.description.split(/\r?\n/).length > 4

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex w-full flex-col space-y-2">
        <div className="flex justify-between">
          <Breadcrumb
            items={breadcrumbs}
            rootIconSize={18}
            rootIcon={IconChoices.FOLDER}
            rootLabel={
              section === 'society'
                ? translate('documentsList.allDocuments', {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  })
                : translate('documentsList.allBoardDocuments', {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  })
            }
          />
        </div>
        <div className="flex justify-between gap-2 items-end">
          <div
            className={
              document?.thumbnail
                ? `flex flex-row gap-8 w-full mb-8`
                : 'relative w-full mb-8'
            }
          >
            {document?.thumbnail && (
              <div className="w-[200px] h-[200px]">
                <Image
                  url={document?.thumbnail}
                  imageClassName="w-full h-full"
                  onClick={openImagePreviewModal}
                />
              </div>
            )}
            <div
              className={`w-4/5 ${shouldShowViewMore ? 'mb-6' : ''} flex flex-col justify-between`}
            >
              <ReactMarkdown
                className={`prose ${!showFullDescription ? 'line-clamp-4' : ''}`}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[[rehypeExternalLinks, { target: 'blank' }]]}
                components={{
                  // eslint-disable-next-line react/no-unstable-nested-components
                  p: ({ ...props }) => (
                    <p className="break-words mb-2 mt-2" {...props} />
                  ),
                }}
              >
                {document?.description}
              </ReactMarkdown>
              {shouldShowViewMore && (
                <button
                  className="pt-2 text-sm cursor-pointer text-brandGreen self-start"
                  onClick={handleFullDescriptionToggle}
                >
                  {showFullDescription
                    ? translate('common.showLess')
                    : translate('common.showMore')}
                </button>
              )}
            </div>
          </div>
          {hasChildDocuments && (
            <IconToggle
              isFirstSelected={_isListMode}
              firstIcon={IconChoices.LIST}
              secondIcon={IconChoices.GRID}
              onChange={onChangeListMode}
            />
          )}
        </div>
        {documents.length === 0 && !document?.description ? (
          <DocumentsListEmptyState
            section={section}
            document={document}
            noSearchResults={noSearchResults}
          />
        ) : (
          <div
            className={`${
              _isListMode ? 'flex flex-col' : 'flex flex-wrap gap-6'
            }`}
          >
            {documents.map((currentDocument) => (
              <DocumentsListItem
                isListMode={_isListMode}
                key={currentDocument._id}
                document={currentDocument}
                documentRoute={documentRoute}
              />
            ))}
          </div>
        )}
      </div>
      <ImagePreviewModal
        showPreviewModal={showImagePreviewModal}
        url={document?.thumbnail || ''}
        onClose={closeImagePreviewModal}
        text={translate('createUpdateFolderForm.labels.thumbnail')}
      />
    </DndProvider>
  )
}
